import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ClosableModal,
  SecondaryButton,
  PrimaryLoadingButton,
  PrimaryTextField,
  ErrorMessage,
  FileDragUploader,
  ContentMediaView,
  PrimaryToggleButtonGroup,
  PrimaryToggleButton,
  PrimaryButton,
  PrimarySelect,
} from 'components';
import { IUploadMediaModalForm, UploadMode, UrlType } from 'pages/media-library/interface';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { UploadResponse, UploadScenarios, FileExtensions } from 'interfaces/file-uploader';
import { mediaLibraryAction } from 'pages/media-library/slice';
import { selectMediaLoading } from 'pages/media-library/selector';
import { selectEnterprise } from 'base/core/selectors';
import { selectAuthUser } from 'base/auth/selectors';
import { getFileDataFromUrl } from 'utils/file-uploader';
import { GenerateVideoThumbnail } from 'base/common';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import { validateYouTubeUrl } from 'utils/youtube-utils';
import YouTubePreview from 'components/organisms/youtube-preview';
import uploadMediaValidationSchema from './validation/add-media';

interface Props {
  open: boolean;
  onClose: () => void;
  path: string;
}

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  FOLDER = 'folder',
  FONT = 'font',
}

const videos = [
  FileExtensions.mp4,
  FileExtensions.flv,
  FileExtensions.mkv,
  FileExtensions.webm,
  FileExtensions.mov,
  FileExtensions.wmv,
];
const images = [FileExtensions.jpg, FileExtensions.jpeg, FileExtensions.png];
const documents = [
  FileExtensions.pdf,
  FileExtensions.ppt,
  FileExtensions.pptx,
  FileExtensions.xls,
  FileExtensions.xlsx,
  FileExtensions.csv,
];
const fonts = [FileExtensions.woff, FileExtensions.woff2];

const extensionToType = new Map<string, string>([]);

videos.forEach((video) => {
  extensionToType.set(video, FileType.VIDEO);
});
images.forEach((image) => {
  extensionToType.set(image, FileType.IMAGE);
});
documents.forEach((document) => {
  extensionToType.set(document, FileType.DOCUMENT);
});
fonts.forEach((font) => {
  extensionToType.set(font, FileType.FONT);
});

const UploadMediaModal = ({ open, onClose, path }: Props) => {
  const dispatch = useDispatch();
  const { id: entId } = useSelector(selectEnterprise);
  const { id: expertId } = useSelector(selectAuthUser);
  const [openThumbnailModal, setOpenThumbnailModal] = React.useState(false);
  const [generatedThumbnailUrl, setGeneratedThumbnailUrl] = React.useState('');
  const [uploadMode, setUploadMode] = React.useState<UploadMode>(UploadMode.FILE);
  const [urlType, setUrlType] = React.useState<UrlType>(UrlType.PUBLIC_URL);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
  } = useForm<IUploadMediaModalForm>({
    resolver: yupResolver(uploadMediaValidationSchema),
    shouldFocusError: true,
  });

  const onSubmit: SubmitHandler<IUploadMediaModalForm> = React.useCallback(
    async (data) => {
      const mediaData = {
        ...data,
        ...{
          path,
          expertId,
          entId,
        },
      };
      await new Promise((resolve, reject) => {
        dispatch(mediaLibraryAction.uploadMedia({
          ...mediaData,
          resolve,
          reject,
        }));
      });
      onClose();
    },
    [path, expertId, entId, dispatch, onClose],
  );

  const handleUpload = (res: UploadResponse) => {
    clearErrors('contentUrl');
    setValue('fileType', res.extension.toUpperCase());
    setValue('fileCategory', res.category ?? null);
    setValue('fileName', res.name);
    if (res.statusCode !== 201) {
      setError('contentUrl', { type: 'custom', message: res.message });
      setValue('contentUrl', '');
      setValue('thumbnailGenerated', '');
    } else {
      setValue('contentUrl', res.url);
    }
  };

  React.useEffect(() => {
    clearErrors();
    reset({
      contentUrl: '',
      title: '',
      description: '',
      fileType: '',
      fileCategory: '',
      fileName: '',
      isExternalFile: false,
      thumbnailGenerated: '',
      urlType: UrlType.PUBLIC_URL,
    });
    setUploadMode(UploadMode.FILE);
    setUrlType(UrlType.PUBLIC_URL);
  }, [open, clearErrors, reset]);

  const loading = useSelector(selectMediaLoading) || false;
  const [buttonDisabled, setButtonDisabled] = React.useState(false);
  const mediaUrl = watch('contentUrl');
  const mediaExt = watch('fileType');
  const mediaCategory = watch('fileCategory');
  const isExternalFile = watch('isExternalFile');

  const getMediaDataFromUrl = async (url: string) => {
    clearErrors('contentUrl');

    try {
      const data = await getFileDataFromUrl(url);
      setValue('fileType', data.fileType);
      setValue('fileCategory', data.fileCategory);
      setValue('fileName', data.fileName);
      setValue('thumbnailGenerated', '');
      setButtonDisabled(false);
    } catch (error: any) {
      setError('contentUrl', { type: 'custom', message: 'The URL is broken, unsupported or blocked by media provider. Only .jpg, .jpeg, .png, .gif, .mp4, .flv, .mkv, .webm, .mov, .wmv, .pdf, .ppt, .pptx, .xls, .xlsx, and .csv formats are allowed.' });
    }
  };

  const getYouTubeDataFromUrl = (url: string) => {
    clearErrors('contentUrl');

    const urlInfo = validateYouTubeUrl(url);
    if (urlInfo.isValid && urlInfo.videoId) {
      setValue('fileType', 'MP4');
      setValue('fileCategory', FileType.VIDEO);
      setValue('fileName', `YouTube Video - ${urlInfo.videoId}`);
      setValue('thumbnailGenerated', '');
      setButtonDisabled(false);
    } else {
      setError('contentUrl', { type: 'custom', message: 'Invalid YouTube URL. Please enter a valid YouTube URL.' });
      setButtonDisabled(true);
    }
  };

  React.useEffect(() => {
    if (isExternalFile && mediaUrl) {
      if (urlType === UrlType.YOUTUBE_LINK) {
        getYouTubeDataFromUrl(mediaUrl);
      } else if (urlType === UrlType.PUBLIC_URL) {
        getMediaDataFromUrl(mediaUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExternalFile, mediaUrl, urlType]);

  React.useEffect(() => {
    if (!mediaUrl) {
      clearErrors('contentUrl');
    }
  }, [mediaUrl, clearErrors]);

  const handleUrlModeChange = (event: any) => {
    const newMode = event.target.value as UploadMode;
    setUploadMode(newMode);

    clearErrors();
    const isExternal = newMode === UploadMode.URL;

    reset({
      contentUrl: '',
      fileType: '',
      fileCategory: '',
      fileName: '',
      isExternalFile: isExternal,
      thumbnailGenerated: '',
      urlType,
    });
  };

  const handleUrlTypeChange = (event: any) => {
    const newUrlType = event.target.value as UrlType;
    setUrlType(newUrlType);
    setValue('urlType', newUrlType);

    setValue('contentUrl', '');
    clearErrors('contentUrl');
  };

  return (
    <ClosableModal
      open={open}
      onClose={onClose}
      title="Upload new media"
      subTitle="Add files to your media library"
      modalClass="media-gallery-modal"
    >
      {openThumbnailModal && mediaUrl && (
        <GenerateVideoThumbnail
          open={openThumbnailModal}
          onClose={() => setOpenThumbnailModal(false)}
          videoUrl={mediaUrl}
          videoExt={mediaExt.replace('.', '').toLowerCase()}
          onFileCreate={({ fileName, url }) => {
            setValue('thumbnailGenerated', fileName);
            setGeneratedThumbnailUrl(url);
            clearErrors('thumbnailGenerated');
            setOpenThumbnailModal(false);
          }}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-3 mt-1">
          <PrimaryToggleButtonGroup
            value={uploadMode}
            fullWidth
            exclusive
            onChange={handleUrlModeChange}
            aria-label="Upload media toggle"
          >
            <PrimaryToggleButton value={UploadMode.FILE}>Upload File</PrimaryToggleButton>
            <PrimaryToggleButton value={UploadMode.URL}>Enter URL</PrimaryToggleButton>
          </PrimaryToggleButtonGroup>
        </div>

        {uploadMode === UploadMode.URL && (
          <div className="mb-3">
            <PrimarySelect
              label="URL Type"
              value={urlType}
              onChange={handleUrlTypeChange}
              options={[
                { id: 1, value: UrlType.PUBLIC_URL, text: 'Public URL (Direct media file link)' },
                { id: 2, value: UrlType.YOUTUBE_LINK, text: 'YouTube URL' },
              ]}
            />
          </div>
        )}
        {isExternalFile ? (
          <>
            {mediaUrl && !errors?.contentUrl && (
              <div className="file-upload-content-media-view d-flex align-items-center justify-content-center mb-2">
                {urlType === UrlType.YOUTUBE_LINK ? (
                  <YouTubePreview
                    url={mediaUrl}
                    height={200}
                    showControls={false}
                  />
                ) : (
                  <ContentMediaView
                    url={mediaUrl}
                    mediaType={mediaCategory ?? ''}
                    thumbnail={generatedThumbnailUrl}
                    fileName={`${mediaUrl?.split('/').pop()}`}
                  />
                )}
              </div>
            )}

            <div className="row">
              <div className={`col-12 ${urlType === UrlType.PUBLIC_URL ? 'col-md-9 col-xl-10 pe-lg-1' : 'col-md-12 col-xl-12'}`}>
                <PrimaryTextField
                  sx={{ mt: 0 }}
                  placeholder={urlType === UrlType.YOUTUBE_LINK ? 'YouTube URL (e.g., https://www.youtube.com/watch?v=...)' : 'Media file URL'}
                  {...register('contentUrl')}
                />
              </div>
              {urlType === UrlType.PUBLIC_URL && (
                <div className="col-12 col-md-3 col-xl-2 ps-lg-1 mt-2 mt-md-0">
                  <PrimaryButton
                    fullWidth
                    onClick={() => setOpenThumbnailModal(true)}
                    disabled={!mediaUrl || mediaUrl === '' || (mediaUrl && mediaCategory !== FileType.VIDEO)}
                  >
                    <PhotoSizeSelectLargeIcon fontSize="small" />
                  </PrimaryButton>
                </div>
              )}
            </div>
            <div className="mb-3 mt-1">
              {errors.contentUrl && <ErrorMessage>{errors?.contentUrl?.message}</ErrorMessage>}
              {!errors.contentUrl && errors.thumbnailGenerated
              && <ErrorMessage>{errors?.thumbnailGenerated?.message}</ErrorMessage>}
            </div>
          </>
        ) : (
          <div className="form-item mb-1">
            <Controller
              control={control}
              name="contentUrl"
              render={() => (
                <FileDragUploader
                  textLabel="Upload file"
                  handleFile={handleUpload}
                  scenario={UploadScenarios.expertContent}
                  currentContentUrl={mediaUrl}
                  uploadAnotherText="Change the media"
                  currentMediaType={mediaCategory ?? ''}
                />
              )}
            />
            <div className="mb-3 mt-1">
              {errors.contentUrl && <ErrorMessage>{errors?.contentUrl?.message}</ErrorMessage>}
            </div>
          </div>
        )}

        <div className="mb-4">
          <PrimaryTextField sx={{ mt: 0 }} placeholder="Media title" {...register('title')} />
          <div className="mb-3 mt-1">
            {errors.title && <ErrorMessage>{errors?.title?.message}</ErrorMessage>}
          </div>

          <PrimaryTextField
            sx={{ height: 70 }}
            rows={3}
            placeholder="Media description"
            {...register('description')}
          />
          <div className="mb-4 mt-1">
            {errors.description && <ErrorMessage>{errors?.description?.message}</ErrorMessage>}
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-6 mb-2 mb-md-0">
            <SecondaryButton fullWidth onClick={onClose}>
              Cancel
            </SecondaryButton>
          </div>
          <div className="col-12 col-md-6">
            <PrimaryLoadingButton fullWidth loading={loading} type="submit" disabled={loading || buttonDisabled}>
              Upload
            </PrimaryLoadingButton>
          </div>
        </div>
      </form>
    </ClosableModal>
  );
};

export default React.memo(UploadMediaModal);
